// src/pages/secondLife/bestand.js
//
// Reine Helfer der Bestandsuebersicht: Sortierung, Filter, Anzeigeformate.
//
// 🔴 Die Regeln stehen HIER, nicht in der Komponente — so sind sie ohne DOM
// pruefbar (Vorbild: `secondLife/rabatt.js`). Die Komponente rendert nur.
//
// Der Zeilen-Vertrag ist verbindlich:
//   docs/superpowers/plans/2026-10-08-second-life-bestandstabelle.md
//   §"Die Zeilenform". Jede Zeile traegt `sku, basis, grade, name, bild, stock,
//   stock_quelle, state, id, kind, hat_produkt, price_eur, vergleichspreis_eur,
//   rabatt_pct, rabatt_vorschlag_pct, rabatt_quelle, rabatt_begruendung,
//   vorschlag, desc, desc_text, description_source, anlegbar, grund,
//   shopify_product_id`.
//
// 🔴 **Die Kennungen und Preisfelder heissen wie in der Warteschlange**
// (Grill-Blocker B1, 2026-10-09): `id` (nicht `listing_id`), `price_eur` (nicht
// `price`), `rabatt_pct` (nicht `rabatt`), dazu `vergleichspreis_eur`,
// `rabatt_vorschlag_pct`, `rabatt_quelle`, `rabatt_begruendung` und `vorschlag`
// mit `{a_vk, ek}`. Grund: `RabattBlock` ist EIN Bauteil fuer beide Flaechen —
// zwei Namen fuer denselben Wert hiessen zwei Vertraege fuer dasselbe Feld. Das
// frueher hier gefuehrte `was` (A-Preis) ist entfallen: `vorschlag.a_vk` traegt
// denselben Wert, und gelesen hat `was` niemand.
//
// 🔴 **`stock_quelle` und `hat_produkt` fehlten hier bis zum 2026-10-09** (Grill W2
// / R15 B2). `stock_quelle` traegt die Herkunft der Bestandszahl (`"shopify"`,
// `"lsm"` oder `null`) und wird von `bestandHerkunft` gelesen; `hat_produkt` ist
// die Bedingung des „Edit"-Knopfes (`beschreibungGrund`).
//
// 🔴 `null` heisst UNBEKANNT, nie 0 — und wird als Geviertstrich gezeigt.
// 🔴 `state` traegt sechs Anzeigewerte; die Route bildet die sechs DB-Werte
//    dorthin ab (§state-Vertrag). Die drei Nicht-Produkt-Zustaende sind seit
//    der Parallel-Bahn EIGENE Werte statt auf `none` gefaltet — `zurueck`
//    (Ruecklaeufer) und `ausgesondert` (Ausschuss) sind unterscheidbar.

export const DEF = 'DEF';

/**
 * Die Obergrenze der Spalte `identity` in px — EINE Quelle fuer die Seite und
 * die Probe (`tests/listen/breite.spec.ts` PW7b).
 *
 * 🔴 **Warum sie hier steht (R15-N4, 2026-11-03):** sie lag als modul-lokale
 * Konstante in `SecondLifeBestand.jsx`, und der Playwright-Test schrieb `480`
 * als Zahl ab. Aendert sich die Grenze, driftete der Test still — er waere
 * gruen geblieben und haette die alte Zahl geprueft. `bestand.js` ist reines JS
 * ohne React, also im Testprozess importierbar.
 *
 * Herleitung: Zellpolster 16 px/Seite ⇒ 448 px nutzbar; `body2` 14 px, ~0,5 em
 * je Zeichen ⇒ ~64 Zeichen/Zeile, zwei Zeilen ⇒ ~128 Zeichen; der laengste
 * gemessene Name hat 120. Die Gegenprobe ist PW7b (Hoehe ≤ 2 Zeilen, kalibriert
 * gegen 200 px).
 */
export const IDENTITY_OBERGRENZE_PX = 480;

// 🔴 **Die TON-Abbildungen der beiden Kartenflaechen — EINE Stelle** (Regel-16-
// Bedingung 5 der Runde 6). Sie standen bis zum 2026-10-09 zeichengleich in
// `SecondLifeBestand.jsx:114` und `OffeneFaelle.jsx:88`; die sechsstellige
// Fassung ist die OBERMENGE der vierstelligen des Karussells, deshalb traegt
// eine Tabelle beide. Zwei Quellen fuer dieselbe Abbildung ziehen beim naechsten
// Zustand mit hoher Wahrscheinlichkeit nur eine — und die Hausform dieses Zweigs
// ist an vielen Stellen ausdruecklich „die EINE Stelle".
export const STUFE_TON = { B: 'warn', C: 'warn', DEF: 'err' };

// Was lebt, ist „ok"; die uebrigen sind neutral — „nicht gelistet" ist eine
// Entscheidung, kein Fehler.
export const ZUSTAND_TON = {
  none: 'plain',
  draft: 'info',
  live: 'ok',
  hidden: 'plain',
  zurueck: 'plain',
  ausgesondert: 'plain',
};

// Stufen, wie die Oberflaeche sie kennt (Spec §5.1). A und LQ kommen nie herein.
export const STUFEN = ['B', 'C', 'DEF'];

// Die sechs Anzeigewerte des `state`-Vertrags. Reihenfolge = Filterleiste.
// 🔴 `ausgesondert` ist AUSSCHUSS (nicht „verworfener Entwurf"), `zurueck` ist
//    ein Ruecklaeufer (`returned`).
export const ZUSTAENDE = ['none', 'draft', 'live', 'hidden', 'zurueck', 'ausgesondert'];

export const ZUSTAND_LABEL = {
  none: 'No product',
  draft: 'Draft',
  live: 'Active',
  hidden: 'Not listed',
  zurueck: 'Returned',
  ausgesondert: 'Scrap',
};

// Der Zustand der Beschreibung (Spec §5.1) — Werte klein, Etiketten so, wie
// die Spec sie nennt: „fehlt · Vorschlag · geaendert".
export const BESCHREIBUNG_LABEL = {
  fehlt: 'Missing',
  vorschlag: 'Suggestion',
  geaendert: 'Changed',
};

/**
 * Sortiert Zeilen. Die DEF-Regel steht VOR jedem Spaltenvergleich:
 * DEF ist sichtbar, aber nicht verkaeuflich — es gehoert ans Ende, in jeder
 * Richtung und bei jeder Spalte (Owner-Entscheid 2026-10-07).
 *
 * `stock` behandelt `null` als UNBEKANNT und legt es auf die Mengenachse UNTER
 * 0 — nicht als 0. In 'desc' steht es damit hinter 0, in 'asc' davor; in beiden
 * Faellen ist es nicht mit 0 verwechselbar.
 */
export function sortiere(zeilen, schluessel = 'stock', richtung = 'desc') {
  const vor = richtung === 'asc' ? 1 : -1;
  return [...zeilen].sort((a, b) => {
    const aDef = a.grade === DEF;
    const bDef = b.grade === DEF;
    if (aDef !== bDef) return aDef ? 1 : -1;

    if (schluessel === 'stock') {
      const av = a.stock == null ? -Infinity : a.stock;
      const bv = b.stock == null ? -Infinity : b.stock;
      if (av !== bv) return (av - bv) * vor;
    } else {
      const av = String(a[schluessel] ?? '');
      const bv = String(b[schluessel] ?? '');
      const c = av.localeCompare(bv, 'de');
      if (c !== 0) return c * vor;
    }
    // Gleichstand: SKU als letzter Schluessel, immer aufsteigend — damit die
    // Reihenfolge stabil bleibt und nicht von der Eingabereihenfolge abhaengt.
    return String(a.sku ?? '').localeCompare(String(b.sku ?? ''));
  });
}

/**
 * Passt ein Wert auf eine Filter-Achse? **Drei Formen, EINE Regel:**
 * `'all'` (keine Einschränkung) · ein Einzelwert (die alte Form) · eine **Liste**
 * von Werten (die G3a-Facette — leer heisst „keine Einschränkung").
 *
 * 🔴 **Rein additiv** (G3a, Plan §2.3/§4): die Facetten „Level" und „Shopify" sind
 * **multi** (`useListQuery`, `kind: 'multi'` liefert immer ein Array — leer im
 * Grundzustand). Die 41 Proben in `bestand.test.js` fahren `'all'` und Einzelwerte
 * und bleiben zeichengleich grün — die Gegenprobe, dass G3 die **Rechenregeln**
 * nicht anfasst. Die Regel steht bewusst HIER und nicht in der Ansicht: der Kopf
 * dieser Datei verspricht, dass die Filterregeln in EINER Datei liegen.
 */
function passtAufAchse(wert, erlaubt) {
  if (Array.isArray(erlaubt)) return erlaubt.length === 0 || erlaubt.includes(wert);
  return erlaubt === 'all' || wert === erlaubt;
}

/**
 * Filtert nach Suche (Artikelnummer ODER Name), Stufe, Zustand und „Stock
 * only". Alle vier greifen gemeinsam; `'all'` schaltet je Achse ab.
 *
 * 🔴 „Stock only" heisst **Bestand > 0**, und `null` (unbekannt) zaehlt
 * NICHT als Bestand. Eine unbekannte Menge ist keine Aussage ueber den
 * Lagerbestand — sie hier als „da" zu lesen waere genau die Verwechslung, gegen
 * die die ganze Spalte antritt.
 *
 * 🔴 `stufe`/`zustand` nehmen seit G3a auch eine **Liste** (die Facette, §2.3) —
 * `passtAufAchse` trägt beide Formen. `'all'` und der Einzelwert bleiben wörtlich.
 */
export function filtere(zeilen, { q = '', stufe = 'all', zustand = 'all', nurBestand = false } = {}) {
  const suche = String(q).trim().toLowerCase();
  return zeilen.filter((z) => {
    if (!passtAufAchse(z.grade, stufe)) return false;
    if (!passtAufAchse(z.state, zustand)) return false;
    if (nurBestand && !((z.stock ?? 0) > 0)) return false;
    if (suche) {
      const heu = `${z.sku ?? ''} ${z.name ?? ''}`.toLowerCase();
      if (!heu.includes(suche)) return false;
    }
    return true;
  });
}

/** Bestand als Text: unbekannt ist ein Geviertstrich, nie eine 0. */
export function bestandText(stock) {
  return stock == null ? '—' : String(stock);
}

/**
 * Wie viele Zeilen tragen **Bestand > 0** — die handlungsfaehige Teilmenge.
 *
 * 🔴 **Dieselbe Lesart wie `filtere(…, { nurBestand: true })` und der
 * Klickdummy** (`17 mit Bestand`). `null` (unbekannt) ist KEIN Bestand: eine
 * unbekannte Menge als „da" zu zaehlen waere genau die Verwechslung, gegen die
 * die ganze Spalte antritt (Regel 22 §3, „unbekannt ist nie 0").
 */
export function mitBestand(zeilen) {
  return (zeilen || []).filter((z) => (z?.stock ?? 0) > 0).length;
}

/**
 * Ist die Bestandsspalte fuer den GANZEN gelieferten Bestand leer?
 *
 * 🔴 **Dieselbe Bedingung wie die Log-Warnung der Route** (Regel-16-W2 der
 * Runde 5, getragen aus Runde 4). `second_life.py:1619` meldet genau dann, wenn
 * `shopify == 0 && lsm == 0` — keine Quelle kennt fuer irgendeine Zeile eine
 * Zahl. Bis hierher sah der Mensch diesen Befund NICHT: die Route zaehlt je
 * Quelle (`bestand_quelle`, ihr eigener Kommentar nennt die Seite als den
 * gemeinten Leser) und schrieb ihn nur ins Log. Auf prod traf genau das zu
 * (gemessen 2026-10-07: **ein** Stufenprodukt fuer 204 Artikel), und die Tabelle
 * zeigte ueberall nur den Geviertstrich — ohne Grund.
 *
 * ⚠️ **Nur echte Nullen zaehlen.** Ein FEHLENDES Feld (`bestand_quelle: {}`)
 * heisst „nicht geliefert", nicht „null" — daraus einen Befund zu bauen waere
 * die Verwechslung, gegen die Regel 22 §3 („unbekannt ist nie 0") antritt.
 */
export function bestandQuelleLeer(quelle) {
  if (!quelle) return false;
  return quelle.shopify === 0 && quelle.lsm === 0;
}

/** Der Satz unter der Filterleiste, wenn `bestandQuelleLeer` wahr ist — im
 *  Klartext der Route (dieselbe Aussage wie ihre Log-Zeile). */
export const LEERE_BESTANDSSPALTE_HINWEIS =
  'The stock column is empty: no source knows a number for any of the rows '
  + '(no Shopify product at this level, LSM without stock).';

/**
 * 🔴 **Wenn LSM nicht geantwortet hat** (2026-10-10).
 *
 * Die Bestandsspalte kommt aus zwei Quellen: Shopify (verkaufbare Menge am
 * LSM-Standort) und LSM (Lagerbestand). Faellt die zweite aus, faellt JEDE
 * Zeile auf Shopify zurueck — und eine Zeile, die nur LSM kennt, steht dann auf
 * „unbekannt". Genau das muss dastehen: sonst sieht ein ausgefallenes Portal
 * aus wie ein Lager ohne Bestand.
 *
 * 🔴 **Der Ausfall nimmt die Ansicht nicht mehr mit.** Bis hierher antwortete
 * die Route mit 500, obwohl der LSM-Login gelungen war und erst der Abruf
 * danach scheiterte — eine Nebenquelle riss die ganze Tabelle mit.
 *
 * 🔴 **Der Satz spricht nur ueber die EIGENE Quelle** (Regel-16 C1/W1,
 * 2026-10-11). Bis dahin hiess es „the stock figures come from Shopify alone" —
 * und fiel Shopify gleichzeitig aus, behauptete der Shopify-Satz daneben das
 * Spiegelbild: zwei Saetze, die einander widersprachen, beide falsch. Was ein
 * LSM-Ausfall wirklich bewirkt (Route `bestand`): Artikel ohne Kladdenzeile
 * fehlen ganz (die Stufen kommen aus LSM), und eine Zahl, die nur LSM kennt,
 * steht auf „unbekannt".
 */
export const LSM_AUSFALL_HINWEIS =
  'LSM did not answer — items only LSM knows about are missing, and stock only '
  + 'LSM knows shows as unknown.';

/**
 * 🔴 **Wenn Shopifys Bestand nicht lesbar war** (2026-10-11, Feld `bestand_fehler`).
 *
 * Das Gegenstueck zu `LSM_AUSFALL_HINWEIS`: faellt die Shopify-Lesung aus, faellt
 * jede Zeile auf LSMs Zahl zurueck, und eine Zeile, die LSM nicht kennt, steht auf
 * „unbekannt". Bis hierher riss genau dieser Ausfall die ganze Ansicht mit — auf
 * prod antwortete die Route seit dem 2026-10-10 mit 500, weil eine numerische
 * `inventory_item_id` ungewandelt an Shopify ging.
 *
 * ⚠️ **Eigene Konstante, NICHT `fehlersatz('shopify_fehler')`.** `shopify_fehler`
 * ist auf dieser Seite der Code des Preis-Schreibwegs („nothing was saved",
 * `fehler.js`) — hier wurde nichts geschrieben, nur nicht gelesen. Deshalb heisst
 * das Feld `bestand_fehler` (Grill W1).
 *
 * Beide Hinweise duerfen gleichzeitig stehen (Plan-Entscheid F2): fallen Shopify
 * UND LSM aus, sind beide Saetze wahr, und zusaetzlich greift
 * `LEERE_BESTANDSSPALTE_HINWEIS` — drei verschiedene Bedingungen, drei Saetze.
 *
 * 🔴 **Und sie widersprechen einander nicht** (Regel-16 C1/W1, 2026-10-11): jeder
 * nennt nur den Ausfall seiner EIGENEN Quelle, keiner behauptet, die Zahlen kaemen
 * aus der anderen. Hier stand „the stock figures come from LSM alone" — im
 * Doppelausfall falsch. Probe: `SecondLifeBestand.test.jsx`, Doppelfall.
 *
 * ⚠️ **Nur der Shopify-Teil loest ihn aus**, nicht ein DB-Fehler davor (Regel-15
 * W1): die Route faengt allein `BestandNichtLesbar`.
 */
export const BESTAND_AUSFALL_HINWEIS =
  'Shopify stock could not be read — stock only Shopify knows shows as unknown.';


/**
 * Die hoechste gueltige Seitennummer fuer `anzahl` Zeilen.
 *
 * 🔴 **Der Riegel gegen eine leere Tabelle** (Regel-16-W3 der Runde 4). Die
 * Seitenweise ist reine ANZEIGE — der Server deckelt weiter nicht (Spec-Entscheid
 * „kein LIMIT", `api/secondLife.js:63-67`). Filtert der Mensch auf wenige
 * Treffer, waehrend er auf Seite 5 steht, ist `seite > maxSeite`; ein blosses
 * `slice` zeigte dann NICHTS. Die Komponente klemmt mit dieser Zahl.
 */
export function maxSeite(anzahl, proSeite) {
  if (!proSeite || proSeite < 1) return 0;
  return Math.max(0, Math.ceil((anzahl || 0) / proSeite) - 1);
}

/**
 * Der Ausschnitt, den die Anzeige zeigt. Reine Funktion, ohne DOM pruefbar.
 *
 * Die Seitennummer wird GEKLEMMT (nicht nur angewandt): eine zu grosse Nummer
 * liefert die letzte Seite, nicht eine leere. Dieselbe Form wie
 * `ProductsTable.jsx` (die Klammer der Seite gegen die Trefferzahl), nur an
 * einer Stelle statt im Bauteil.
 */
export function seiteVon(zeilen, seite, proSeite) {
  const liste = zeilen || [];
  const n = Math.min(Math.max(0, Math.floor(seite) || 0), maxSeite(liste.length, proSeite));
  return liste.slice(n * proSeite, (n + 1) * proSeite);
}

/** Preis in Euro, deutsch formatiert; unbekannt ist ein Geviertstrich. */
export function euroText(betrag) {
  if (betrag == null || betrag === '' || Number.isNaN(Number(betrag))) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(betrag));
}

/**
 * Der **durchgestrichene** Vergleichspreis — oder `null`, wenn es keinen gibt.
 *
 * 🔴 **`null` heisst hier „nichts hinschreiben"** (Klickdummy-Nachzug, Punkt 2),
 * nicht „Geviertstrich": ein fehlender Vergleichspreis ist keine unbekannte
 * Zahl, sondern die Aussage „diesen Artikel gab es nie teurer". Der
 * Geviertstrich, den `euroText(null)` liefert, waere an dieser Stelle ein
 * zweiter Zustand fuer dieselbe Sache.
 */
export function vergleichspreisText(zeile) {
  const v = zeile?.vergleichspreis_eur;
  if (v == null || v === '') return null;
  const text = euroText(v);
  return text === '—' ? null : text;
}

/**
 * Der **Frische-Stempel** des Kopfes — „Stock as of 04:30 UTC".
 *
 * 🔴 **Die Zeit ist UTC, und das ist der Punkt** (Klickdummy-Nachzug, Punkt 1):
 * `geholt_am` kommt als ISO-Zeit mit Zone von der Route; `getHours()` zeigte
 * die Zeit der MASCHINE des Betrachters und waere damit eine andere Aussage.
 * Ein Stempel, der je nach Rechner anders lautet, ist kein Stempel.
 *
 * 🔴 **`null` heisst „kein Stempel"**, nicht „00:00" — eine Antwort ohne
 * `geholt_am` (aeltere Fassung, Fehlerpfad) darf keine erfundene Uhrzeit
 * behaupten.
 */
/**
 * Das **Alter einer Kladdenzeile** — „today" / „yesterday" / „3 days ago".
 *
 * 🔴 **Klickdummy-Nachzug, Punkt 6.** Der Dummy traegt das Alter auf der Karte
 * („heute" / „vor 3 Tagen"). Auf einem Feld, das „Wartet auf dich" heisst, ist
 * Alter das erste Sortierbeduerfnis.
 *
 * ⚠️ **Gerechnet wird gegen „jetzt", und „jetzt" ist der BETRACHTER** — anders
 * als `stempelText`, der UTC sein MUSS (er beschreibt einen Serverlauf). Ein
 * Alter ist eine Aussage ueber den Betrachter, kein Zeitstempel des Servers.
 * Deshalb ist `jetzt` ein Parameter: eine Probe kann ihn festnageln, ohne die
 * Uhr der Maschine zu treffen.
 *
 * 🔴 `null` heisst **„kein Datum"**, nicht „0 Tage" — eine Zeile ohne
 * `erfasst_am` bekommt keinen erfundenen Wert.
 */
export function alterText(erfasstAm, jetzt = Date.now()) {
  const roh = String(erfasstAm ?? '').trim();
  if (!roh) return null;
  const t = new Date(roh).getTime();
  if (Number.isNaN(t)) return null;
  const tage = Math.max(0, Math.floor((jetzt - t) / 86400000));
  if (tage === 0) return 'today';
  if (tage === 1) return 'yesterday';
  return `${tage} days ago`;
}

export function stempelText(geholtAm) {
  const roh = String(geholtAm ?? '').trim();
  if (!roh) return null;
  const d = new Date(roh);
  if (Number.isNaN(d.getTime())) return null;
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `Stock as of ${hh}:${mm} UTC`;
}

/**
 * 🔴 **Der Zusatz des Stempels, wenn eine BESTANDSQUELLE nicht geantwortet hat**
 * (Regel-16-W1 der Runde 1, 2026-10-14).
 *
 * Der Stempel sagt „**Stock** as of 13:47 UTC" — er nennt den BESTAND, nicht den
 * Abruf. Bei ausgefallenem LSM ist der Abruf frisch, der Bestand aber nur
 * teilweise bekannt (die Zeilen kommen dann aus Shopify, eine Zahl, die nur LSM
 * kennt, steht auf „unbekannt"). Derselbe Satz ist ebenso schief, wenn Shopifys
 * Bestand nicht lesbar war (`bestand_fehler`) — deshalb deckt der Zusatz
 * **beide** Quellen ab und nennt sie beim Namen. Ohne ihn stempelt der Kopf einen
 * vollstaendigen Bestandsstand, den die Antwort nicht traegt.
 *
 * 🔴 **`null` heisst „kein Zusatz"** — bei zwei gesunden Quellen bleibt der Satz
 * unveraendert (Kalibrierung: ohne diese Haelfte waere ein immer angehaengter
 * Zusatz genauso gruen, und der Stempel behauptete in JEDER Lage einen Ausfall).
 * ⚠️ Ein LEERER Fehlerstring zaehlt nicht als Ausfall — dieselbe Lesart wie
 * `lsm_fehler === null` in der Route („gelesen").
 */
export function stempelZusatz({ lsmFehler, bestandFehler } = {}) {
  const fehlen = [];
  if (lsmFehler) fehlen.push('LSM');
  if (bestandFehler) fehlen.push('Shopify');
  if (fehlen.length === 0) return null;
  return `${fehlen.join(' and ')} missing`;
}

/** Rabatt als „−25 %" mit Minuszeichen (U+2212), wie der Klickdummy. */
export function rabattText(pct) {
  if (pct == null || pct === '' || Number.isNaN(Number(pct))) return null;
  const n = Number(pct);
  const ziffern = Number.isInteger(n) ? 0 : 1;
  return `−${n.toLocaleString('de-DE', { maximumFractionDigits: ziffern })} %`;
}

/** Der sichtbare Grund einer gesperrten Zeile kommt aus `zeile.grund` — die
 *  Route bildet ihn (`second_life_bestand.GRUND_DEF` / `GRUND_KEIN_PRODUKT`).
 *  🔴 Hier stand frueher ein ZWEITER Wortlaut fuer DEF (`DEF_GRUND`); das war
 *  eine zweite Quelle fuer denselben Satz (Regel-15-Befund W2). Es gibt jetzt
 *  genau eine: das Vertragsfeld `grund`. */

/**
 * Die Herkunft der Bestandszahl — `'lsm'`, wenn die Menge aus LSM kommt, sonst
 * `null`. Eine LSM-Menge ist eine Lagermenge, keine verkaufbare Menge am
 * Shopify-Standort; wer sie fuer verkaufbar haelt, ueberverkauft. Deshalb wird
 * sie sichtbar gekennzeichnet, wo sie steht.
 */
export function bestandHerkunft(zeile) {
  return zeile?.stock_quelle === 'lsm' ? 'LSM' : null;
}

// 🔴 **`empfehlungPct` und `empfehlungGrund` sind am 2026-10-14 GELOESCHT**
// (Plan §7.2, eigener Commit). Sie hatten **keinen** Produktionsimporteur: die
// einzige Erwaehnung ausserhalb dieser Datei war ein Kommentar in
// `OpenCases.jsx`, der festhielt, dass die Karte die Empfehlung **seit dem
// 2026-10-12** nicht mehr rechnet — sie zeigt den `RabattBlock` mit Vorschlag
// und Begruendung, und zwei Fassungen derselben zwei Zahlen nebeneinander waeren
// die zweite Quelle, die dieser Zweig ueberall vermeidet.
//
// 🔴 **Die Begruendung des Schnitts ist „toter Kode seit dem 2026-10-12", NICHT
// dieser Umbau** (Grill R1, W9): die lesende Zweitfassung der Empfehlung ist
// damals entfallen. Er faehrt als eigener Commit mit, weil er dieselbe Datei
// beruehrt und klein ist. Probe: `grep -rn "empfehlungPct\|empfehlungGrund"
// frontend/src --include='*.jsx' --include='*.js'` findet nach diesem Commit
// **nichts** mehr.

// Die Herkunfts-Token, die `second_life._quellen` schreibt, in Klartext.
// 🔴 **Die Uebersetzung steht HIER und nicht im Backend** — die Route reicht den
// rohen Token durch (wie die Warteschlange), und eine zweite Uebersetzung dort
// waere eine zweite Quelle fuer denselben Satz. Was hier fehlt, faellt sichtbar
// auf sich selbst zurueck (`quelleText`), statt zu verschwinden.
export const QUELLE_LABEL = {
  note: 'the stock note',
  pruefnotiz: 'the LSM check notes',
  sammlung: 'the return reasons of the collection',
  reason: 'the coded return reason',
  grade: 'the quality level',
};

/** „a", „a and b", „a, b and c" — die Aufzaehlung des Quellensatzes. */
function aufzaehlen(texte) {
  if (texte.length <= 1) return texte[0] ?? '';
  return `${texte.slice(0, -1).join(', ')} and ${texte[texte.length - 1]}`;
}

/**
 * Die Herkunft eines Beschreibungs-Entwurfs in Klartext — „Suggestion from the
 * LSM check notes" (Klickdummy-Nachzug, Punkt 5).
 *
 * 🔴 **Ein unbekanntes Token wird NICHT verschwiegen.** Es steht roh im Satz
 * (`Suggestion from foo`). Eine Quelle, die stillschweigend weggelassen wird,
 * sieht aus wie „keine Quelle" — und genau das ist die Verwechslung, gegen die
 * die Herkunftsaussage gebaut ist (`llm-fallback-provenance.md`).
 *
 * 🔴 **Leer heisst `null`** — ein von Hand geaenderter Entwurf traegt
 * ausdruecklich keine Quelle, und dann steht dort nichts.
 */
export function quelleText(source) {
  const roh = String(source ?? '').trim();
  if (!roh) return null;
  const teile = roh.split('+').map((t) => t.trim()).filter(Boolean);
  if (!teile.length) return null;
  return `Suggestion from ${aufzaehlen(teile.map((t) => QUELLE_LABEL[t] || t))}`;
}

/** Der Grund, den eine Ausschuss-Zeile sichtbar traegt. Die Route setzt hier
 *  `grund: null`, weil `anlegbar` fuer eine `discarded`-Zeile wahr bleibt —
 *  der Satz hat also keine zweite Quelle im Vertrag. */
export const AUSSCHUSS_GRUND = 'Scrap — the item is not sold';

/** 🔴 **Der neue Grund (Grill-Blocker B2, 2026-10-09).** Eine `discovered`-Zeile
 *  hat eine Kladde, aber kein Shopify-Produkt: die Route antwortet 404
 *  `zeile_fehlt`, und der Satz dazu („Dieser Fall hat keine Kladdenzeile") ist
 *  das Gegenteil der Wahrheit. Auf prod sind **12 von 13** Kladdenzeilen
 *  `discovered` — der Fall ist der Normalfall, nicht die Ausnahme. */
export const OHNE_PRODUKT_GRUND = 'No Shopify product — create it first via “Create”';

/**
 * Warum der „Edit"-Knopf gesperrt ist — oder `null`, wenn er bedienbar ist.
 *
 * 🔴 **Je Grund genau EINE Quelle** (Regel-15-Befund W2: ein zweiter Wortlaut
 * fuer denselben Satz ist ein zweiter Satz):
 *   * DEF          → `zeile.grund`, das Vertragsfeld der Route
 *   * Ausschuss    → `AUSSCHUSS_GRUND`
 *   * kein Produkt → `OHNE_PRODUKT_GRUND`
 *
 * 🔴 **`hat_produkt !== true` ist fail-closed.** Fehlt das Feld (alte Antwort),
 * wird gesperrt statt ins Leere geschickt. Die Sperre ist sichtbar — ein
 * gesperrter MUI-Knopf hat `pointer-events: none`, ein `title` kaeme nie an.
 */
export function beschreibungGrund(zeile) {
  if (!zeile) return null;
  if (zeile.grade === DEF) return zeile.grund || null;
  if (zeile.state === 'ausgesondert') return AUSSCHUSS_GRUND;
  if (zeile.state === 'live') return VEROEFFENTLICHT_GRUND;
  if (zeile.hat_produkt !== true) return OHNE_PRODUKT_GRUND;
  return null;
}

/**
 * Der Grund, der an einer VEROEFFENTLICHTEN Zeile steht.
 *
 * 🔴 **Befund der Backend-Bahn B8 (Runde 6).** `POST /<id>/beschreibung` schreibt
 * mit `status=None` **in das Produkt** — bei `live` waere der Text sofort im Shop
 * sichtbar, an der Freigabe-Stufe vorbei. Die Route hat deshalb seit Runde 6 ein
 * Zustands-Gate und antwortet 409 `zustand`. Die Oberflaeche sperrte `live` aber
 * **nicht**: solange `SECOND_LIFE_BESCHREIBUNG_AKTIV` aus ist (prod), faellt das
 * nicht auf, weil dann ohnehin alles gesperrt ist — **mit dem Einschalten** waere
 * der Knopf bedienbar gewesen und in eine Absage gelaufen.
 *
 * ⚠️ Eine gesperrte Flaeche ohne Grund ist die Hausform, die diese Seite nicht
 * will: der Grund steht als sichtbarer Text unter dem Knopf.
 *
 * 🔴 **Der Satz nennt ABSICHTLICH keinen Weg heraus** (Regel-15-Befund C1 der
 * Runde 6). Die erste Fassung sagte: erst ausblenden, dann bearbeiten — und
 * **es gibt kein Ausblenden**: die Routen des Blueprints sind `/queue`,
 * `/bestand`, `/rabatt`, `/rabatt-vorschlag`, `/approve`, `/anlegen`,
 * `/discard`, `/beschreibung`. **Keine** `/verstecken`-Route, und die
 * Oberflaeche hat keine solche Handlung; `verstecke_listing` laeuft nur im Cron
 * (`verstecke_verkaufte`). Ein Grund, der einen Weg nennt, den es nicht gibt,
 * ist genau die Klasse, die dieser Zweig zweimal als Blocker gefuehrt hat.
 *
 * ⚠️ **Die Spec §5.2 verlangt Ausblenden/Wecken als Route; sie ist nicht
 * gebaut.** Der Owner hat den Knopf ausdruecklich abgelehnt. Solange es ihn
 * nicht gibt, sagt der Grund WARUM gesperrt ist — nicht WIE man herauskommt.
 */
export const VEROEFFENTLICHT_GRUND =
  'Published — changes would be visible in the shop immediately';

/**
 * Der Grund, der an einem Knopf steht, der ohne Rabatt in eine Absage liefe.
 *
 * 🔴 **Der Satz nennt seit dem 2026-10-14 den ⋮ DIESER ZEILE** (Grill WV1, N3).
 * Zwei Umzüge liegen davor: bis zum 2026-10-12 schickte er den Bediener mit
 * „in the table below via ‚Edit'" auf eine andere Flaeche derselben Seite — die
 * Sperre als Sackgasse; danach hiess der Ort „this card", weil die Karte den
 * `RabattBlock` unmittelbar ueber dem gesperrten Knopf trug. Mit dem Umbau auf
 * die gruppierte Liste (OE-SL1) wandert der Rabatt in einen **⋮-Dialog**
 * (§1.6 des Plans) — „in this card" zeigt damit auf eine Flaeche, die es nicht
 * mehr gibt. Wer den Ort erneut verschiebt, zieht diesen Satz mit; die Proben
 * `frontend/tests/products/second-life-bestand.spec.ts` (B.17) und
 * `bestand.test.js` pinnen ihn.
 *
 * 🔴 **Und das ZEICHEN `⋮` steht seit dem 2026-10-14 nicht mehr im Satz**
 * (Regel-16-W4). Der Satz ist die Begruendung eines gesperrten Knopfes und wird
 * vorgelesen; ein Screenreader liest `⋮` als „vertical ellipsis" oder
 * ueberspringt es. Statt des Zeichens steht dort der **zugaengliche Name des
 * Menueknopfs** dieser Seite — `Actions for <Kennung>` (`OpenCases.jsx`,
 * `RowActionsMenu label`), dessen stabiler Wortlaut `Actions` ist. 🔴 **Sichtbar
 * ist auf dieser Flaeche allein das `⋮`**; das Wort `Actions` traegt sichtbar nur
 * der Kopf der Aktionsspalte der Bestandstabelle (`SecondLifeBestand.jsx`).
 * Hier hoert es allein der Bildschirmleser am Knopf. `bestand.test.js` haelt
 * fest, dass kein `⋮` mehr im Satz steht.
 *
 * ⚠️ **Wortlaut ist ein VORSCHLAG.** Sichtbarer Text geht dem Owner vor; die
 * Aussage („der Weg liegt hier, nicht woanders") ist die Zusicherung, der Satz
 * ist es nicht. Der Orchestrator hat den ORT als Arbeitsannahme gesetzt
 * (Plan §8 Nr. 4); die Wahl des Wortes statt des Zeichens ist der Entscheid aus
 * Regel 16 W4.
 */
export const OHNE_RABATT_GRUND =
  'Save the discount first — it is under "Actions" in this row.';

/**
 * Der Grund, der an einer Zeile steht, die keine Aggregat-Zeile ist.
 *
 * 🔴 **Grill Q2 / Regel-15-W1 / Regel-16-W1 der Runde 4, alle drei unabhaengig.**
 * `lege_listing_an` bricht fuer jede Zeile ab, deren `kind` nicht `aggregate`
 * ist (`backend/second_life.py:954-955` → 409 `keine_aggregat_zeile_<kind>`),
 * und `POST /<id>/rabatt` ebenso. Ohne eine eigene Sperre blieb „Create"
 * BEDIENBAR und der Klick endete in einer Absage — genau die Hausform, gegen
 * die der Rabatt-Blocker der Runde 3 gebaut wurde.
 *
 * 🔴 **Fail-closed: ein FEHLENDES `kind` ist keine Erlaubnis.** Der Server liest
 * `(zeile.kind or "unit")` und behandelt ein fehlendes Feld als Einheiten-Zeile
 * (`:955`, dieselbe Lesart in `second_life_bestand._listing_index`). Nur ein
 * ausdrueckliches `aggregate` oeffnet den Weg — die Pruefung im Bauteil lautet
 * deshalb `kind !== 'aggregate'`, nicht `kind === 'unit'`.
 */
export const KEIN_AGGREGAT_GRUND =
  'This unit row is merged into an aggregate row on the next detection run '
  + '— only then can it be created.';

/**
 * Der SPERRGRUND einer kladdenfreien Zeile in einer UNMOEGLICHEN Lage — eine
 * Zeile ohne Kladde (`id == null`), die der Rabattweg trotzdem nicht freigibt
 * (`rabattMoeglich`). Die Route erzeugt diese Lage nicht.
 *
 * 🔴 **Bis zum 2026-10-10 trug diese Karte einen `RouterLink` auf
 * `/second-life`** — mit dem Text „Create in the queue". Seit die
 * Warteschlange stillgelegt ist (`/second-life` IST die Bestandsansicht),
 * zeigte der Knopf auf die eigene Seite, und die Warteschlange hat diese
 * Lage ohnehin nie behandelt (`grep -n "ohneKladde" SecondLifeQueue.jsx` war
 * **leer**). Der Weg war eine Sackgasse, bevor er eine wurde.
 *
 * 🔴 **Die Ursache ist gemessen, nicht behauptet:** eine Kladde
 * (`second_life_listings`) entsteht heute aus einem `rma_cases`-Fall mit
 * B/C-`stock_grade` und LSM-RT-Nummer (`backend/second_life.py::_kandidaten`,
 * `SELECT … FROM rma_cases WHERE stock_grade = ANY(:grade)`; `erkenne()` legt
 * daraus die `discovered`-Zeile an, `docs/features/second-life.md` §Die
 * Erkennung) oder als Aggregat aus vorhandenen `unit`-Zeilen (`aggregiere()`).
 *
 * 🔴 **DER HINWEIS IST SEIT DEM 2026-10-15 `OHNE_KLADDE_HINWEIS`** (s. dort;
 * Wortlaut seit dem 2026-10-16 der des Owners von diesem Tag). Dieser Satz hier
 * ist nur noch der Sperrgrund der unmoeglichen Lage — der HINWEIS an der Zeile
 * traegt einen anderen Wortlaut.
 *
 * ⚠️ **Wie es dazu kam, damit die Rolle nicht wieder verwischt.** Bis zum
 * 2026-10-14 sperrte dieser Satz den Hauptknopf (`OpenCases.zeilenLage`), weil
 * `createSecondLife(id)` eine Kladden-Kennung braucht und es keinen Weg gab,
 * eine zu erzeugen. **Diesen Weg gibt es seit dem 2026-10-14** — `POST
 * /api/second-life/kladde` legt die Aggregat-Zeile aus der SKU an
 * (`SecondLifeBestand.stelleKladdeSicher`), fail-closed ueber `produkt_fuer_sku`.
 * Der Satz wurde damit vom Sperrgrund zum **Hinweis**; sein Wortlaut beschrieb
 * aber weiter die alte Sperre („eine Retoure legt einen an") — eine Bedingung,
 * die keine mehr war.
 *
 * 🔴 **Seither hat er genau EINEN Leser:** den Fail-closed-Zweig in
 * `OpenCases.zeilenLage` (`widerspruch = ohneKladde && !rabattMoeglich`). Eine
 * kladdenfreie Zeile, die der Rabattweg nicht freigibt, ist ein Widerspruch; er
 * sperrt sie mit diesem Satz. **Die Route erzeugt diese Lage nicht** — Kennung
 * und Zeilenart kommen aus derselben Kladde (`second_life_bestand._bau`); sie
 * ist die Antwort auf eine unmoegliche Fixture, nicht auf einen Betriebsfall.
 *
 * ⚠️ **Der Wortlaut bleibt woertlich stehen**, weil ihn dieser Zweig pinnt
 * (`OpenCases.test.jsx` „B2: eine kladdenfreie Zeile MIT Zeilenart bleibt
 * gesperrt", `OpenCases.grund.test.jsx` als Kalibrierung).
 */
export const OHNE_KLADDE_GRUND = 'No case yet — a return creates one.';

/**
 * Der HINWEIS an einer kladdenfreien Zeile (`id == null`) — **kein Sperrgrund**.
 *
 * 🔴 **Wortlaut vom Owner, 2026-10-16, woertlich uebernommen.** Kein Zeichen
 * geaendert, nicht uebersetzt, nicht umformuliert. Er loest den Vorgaenger-Satz
 * ab, den derselbe Owner am 2026-10-15 entschieden hatte (Karte A1 der Uebergabe
 * `docs/superpowers/plans/2026-10-14-second-life-offene-themen.md`).
 *
 * ⚠️ **Warum ersetzt, obwohl auch der Vorgaenger vom Owner kam.** Der Vorgaenger
 * behauptete, es gebe fuer diesen Artikel **keine Retoure**. Die Bedingung, unter
 * der der Satz erscheint (`z.id == null`), stellt aber nur **keine Kladde** fest —
 * und das ist **nicht dasselbe** (zwei belegte Gegenwege, s. unten). Der Satz war
 * damit an genau den Zeilen falsch, fuer die er gedacht war. Der neue nennt nur,
 * was die Bedingung **wirklich** feststellt: keine Pruefnotiz, also kein
 * Vorschlag, also **von Hand**.
 *
 * 🔴 **Er ist trotzdem NICHT „immer wahr" ueber den Artikel** (Regel-16-Befund
 * W1, 2026-10-19). `rma_cases.stock_grade_note` kann **ohne Kladde** existieren —
 * `backend/rma_routes.py:10664` setzt sie aus dem Fall, unabhaengig von
 * `second_life_listings`. Wahr ist der Satz fuer den **Weg dieser Flaeche**:
 * `schlage_rabatt_vor` endet ohne Kladde bei `kein_pruefbefund`. Er beschreibt
 * die fehlende Notiz **an dieser Zeile**, nicht eine Eigenschaft des Artikels.
 * Dieselbe Aussage steht in `docs/features/second-life.md` §Rabatt und Preis.
 *
 * ⚠️ **Er sagt etwas, was die Zeile darueber nicht schon sagt.** An derselben
 * Zeile steht `OHNE_RABATT_GRUND` („Save the discount first — it is under
 * \"Actions\" in this row."); der nennt den WEG. Dieser nennt den GRUND, warum
 * es hier keinen Vorschlag gibt:
 *
 *   * Ohne Kladde kommt **an dieser Zeile keine Pruefnotiz an** — der Weg der
 *     Flaeche endet ohne Kennung (`backend/second_life.py`, `lege_kladde_an`:
 *     „`lsm_rt_number` bleibt leer"; `docs/features/second-life.md` §Rabatt und
 *     Preis). Die Notiz der Stuecke (`rma_cases.stock_grade_note`) kann am Fall
 *     trotzdem existieren; sie erreicht diese Zeile nur nicht.
 *   * Ohne Notiz endet `schlage_rabatt_vor` bei `kein_pruefbefund`
 *     (`backend/second_life_rabatt.py:250`) — **kein geratener Rabatt**.
 *   * Also ist der Rabatt hier **von Hand** zu setzen. Genau das sagt der Satz.
 *
 * 🔴 **Die Bedingung ist die fehlende KLADDE, nicht die fehlende RETOURE — und
 * das ist nicht dasselbe.** „Kladdenfrei" (`z.id == null`) heisst nur, dass fuer
 * dieses (Artikel, Stufe) keine `second_life_listings`-Zeile dasteht. Es gibt
 * zwei belegte Wege, auf denen das OHNE fehlende Retoure gilt — beide sind der
 * Grund, warum der Satz von der **fehlenden Pruefnotiz** spricht und nicht von
 * der Retoure:
 *
 *   1. **Der Erkenner verlangt eine RT-Nummer.** `_kandidaten`
 *      (`backend/second_life.py:186-190`) liest `rma_cases` mit
 *      `lsm_rt_number IS NOT NULL AND lsm_rt_number <> ''`. Ein B/C-Fall ohne
 *      RT-Nummer wird nie Kandidat — die Zeile bleibt kladdenfrei, **obwohl eine
 *      Retoure existiert**.
 *   2. **Zeitluecke bis zum Tageslauf.** Die Retoure liegt in `rma_cases`,
 *      `erkenne`/`aggregiere` laufen aber erst im Tageslauf
 *      (`backend/cron_jobs/run_daily_sync.py:185` →
 *      `run_second_life_detection`). Dazwischen ist die Zeile kladdenfrei.
 *
 * ⚠️ **Keiner der zwei Wege aendert das Verhalten** — sie erklaeren nur, worueber
 * der Satz spricht. Wer ihn liest, soll „keine Kladde" und „keine Retoure"
 * nicht ineinanderlaufen lassen.
 *
 * 🔴 **Und er sperrt nichts.** Er traegt eine eigene `id` (zweites Ziel des
 * `aria-describedby` am Knopf, Regel-16-Befund W2), ist aber nie der
 * Sperrgrund — der ist der fehlende Rabatt. `OpenCases.zeilenHinweis` vergleicht
 * seine Bedingung deshalb weiter gegen `OHNE_KLADDE_GRUND` (den GRUND), nicht
 * gegen diesen Satz: nur so steht der Text im Widerspruchs-Fall genau EINMAL im
 * DOM.
 */
export const OHNE_KLADDE_HINWEIS =
  'No inspection note for this item — set the discount by hand.';

/**
 * Der Schluessel EINER Zeile fuer den **zeilenweisen** Beschaeftigt-Zustand.
 *
 * 🔴 **Warum die SKU und nicht die Kladden-Kennung** (Auftrag 2026-10-14).
 * Der Owner: *„Wenn ich bei einem Artikel auf Create druecke, dann rotieren alle
 * Buttons — warum? Ich moechte dass ich das einzeln anklicken kann und dann auch
 * weitere und die sich einreihen von selbst."* Bis dahin fuehrte die Seite EINEN
 * Zustand fuer die ganze Liste.
 *
 * ⚠️ **Die SKU ist der stabilere der beiden Kandidaten.** Eine kladdenfreie
 * Zeile hat `id == null` und **bekommt** eine Kennung, sobald die Kladde
 * angelegt ist (`stelleKladdeSicher`) — mit `id` als Schluessel faellt der
 * Fortschritt genau in dem Moment weg, in dem die Zeile entsteht. Die SKU
 * (`<basis>@<stufe>`) ist ueber beide Zustaende dieselbe; die Route setzt sie
 * unbedingt (`second_life_bestand._bau`).
 *
 * ⚠️ **Der Rueckfall auf `id` ist fuer Proben-Fixtures ohne `sku`** — nicht fuer
 * den Betrieb. Zwei Zeilen ohne beides ergaeben denselben leeren Schluessel;
 * das ist der eine Fall, den diese Funktion nicht unterscheiden kann, und er
 * kommt aus der Route nicht vor.
 */
export function zeilenSchluessel(zeile) {
  const sku = String(zeile?.sku ?? '').trim();
  if (sku) return sku;
  return zeile?.id == null ? '' : String(zeile.id);
}

/**
 * Laeuft an DIESER Zeile gerade etwas — und was?
 *
 * Gibt die Art zurueck (`'anlegen' | 'freigeben' | 'verwerfen'`) oder `null`.
 *
 * 🔴 **`Object.prototype.hasOwnProperty.call`, nicht `in`.** Die Schluessel
 * dieser Abbildung sind SKUs aus der Serverantwort; ein `in`-Test waere fuer
 * `'constructor'` wahr und liesse eine Zeile mit dieser SKU als „laeuft" gelten.
 * Dieselbe Wache wie in `fehler.js::fehlersatz` (`typeof … === 'string'`).
 */
export function laufArt(laufend, zeile) {
  const karte = laufend || {};
  const schluessel = zeilenSchluessel(zeile);
  if (!Object.prototype.hasOwnProperty.call(karte, schluessel)) return null;
  return karte[schluessel] ?? null;
}

/** Laeuft an dieser Zeile gerade etwas? Der Wahrheitswert zu `laufArt`. */
export function laeuftAn(laufend, zeile) {
  return laufArt(laufend, zeile) != null;
}

/**
 * Der Fortschrittstext eines laufenden Vorgangs — je ART, nicht je Zeile.
 *
 * 🔴 **Die Tabelle ist neu am 2026-10-14** (Regel-16-Befund B1). Bis dahin stand
 * der Text als zwei Ternaerausdruecke IM Knopf, und beide kannten nur
 * `anlegen`/`freigeben`. Die dritte Art — `kladde`, das Anlegen der
 * Kladdenzeile vor dem Rabatt-Dialog — fiel damit durch: **kein Spinner, keine
 * Beschriftung, kein Zeichen**, und der Nutzer sah nach „Actions → Discount…"
 * nur, dass sich das Menue schloss. Der Vorgang war stumm.
 *
 * ⚠️ **Der Vertrag steht in der Schnittstelle** (`OpenCases`, Dateikopf) — eine
 * vierte Art gehoert in BEIDE Stellen, sonst ist sie wieder stumm.
 *
 * 🔴 **„Preparing…" ist ein OWNER-ENTSCHEID — am 2026-10-14 BESTAETIGT.**
 * „Creating…" waere an einem Knopf, der nichts anlegt, eine Luege (er legt die
 * Kladde an, nicht das Produkt) — deshalb dieses Wort. Der Wortlaut ist
 * Kundentext und damit Samuels Sache; er hat ihn am 2026-10-14 so bestaetigt
 * („Passt"). Wer den Text aendert, aendert nur diese Zeile.
 *
 * 🔴 **Die Tabelle steht seit dem 2026-10-16 HIER, nicht mehr in `OpenCases.jsx`**
 * — sie hat einen zweiten Leser bekommen: die sammelnde Live-Region des
 * Aufrufers (`laufendeAnsage`). Zwei Abschriften derselben Woerter waeren genau
 * die Doppelung, die `RabattBlock`s Ein-Baustein-fuer-zwei-Flaechen-Regel
 * vermeidet.
 */
export const FORTSCHRITT_TEXT = {
  anlegen: 'Creating…',
  freigeben: 'Releasing…',
  kladde: 'Preparing…',
};

/** Der Text zur Art — `null`, wenn diese Art KEINEN Fortschritt zeigt.
 *
 * ⚠️ `hasOwnProperty`, nicht `in`: die Art kommt aus der Abbildung des
 * Aufrufers, und `FORTSCHRITT_TEXT['constructor']` waere sonst eine Funktion
 * (dieselbe Wache wie in `bestand.laufArt` und `fehler.js::fehlersatz`).
 *
 * 🔴 **`verwerfen` faellt hier absichtlich durch** (der Docstring ueber der
 * Tabelle nennt den Grund): der Hauptknopf dieser Zeile tut beim Ablegen nichts,
 * ein Fortschrittstext an ihm waere eine Luege. Dieselbe Grenze gilt fuer die
 * Ansage (`laufendeAnsage`) — ein Vorgang ohne sichtbaren Fortschritt wird auch
 * nicht angesagt.
 */
export function fortschrittText(art) {
  if (!Object.prototype.hasOwnProperty.call(FORTSCHRITT_TEXT, art)) return null;
  return FORTSCHRITT_TEXT[art] || null;
}

/**
 * Die EINE Sammelansage ueber alle laufenden Zeilen — der Text der Live-Region.
 *
 * 🔴 **Warum es sie gibt** (Befund „die Zeile als Arbeitseinheit", Luecke 1):
 * seit dem 2026-10-14 laufen die Zeilen EINZELN und damit PARALLEL. Bis dahin
 * drehte hoechstens eine Zeile, und „hier laeuft etwas" war eine Aussage ueber
 * die Liste. Jetzt ist es eine Aussage ueber N Zeilen — und ohne diese Ansage
 * hoert eine Sprachausgabe die Vorgaenge gar nicht: der sichtbare Text steht im
 * Knopf, nicht in einer Region (`ProgressButton.jsx`, Dateikopf).
 *
 * 🔴 **EINE Region, nicht eine je Zeile — und das ist gemessen, nicht Geschmack.**
 * `offeneFaelle()` (`offeneFaelleAuswahl.js:39`) hat **keine Obergrenze**: die
 * Zahl der gleichzeitig moeglichen Zeilen ist „so viele offene Faelle wie es
 * gibt", und `OpenCases` rendert die Gruppen vollstaendig. Eine Region je Zeile
 * waere damit bis zu N Live-Regionen auf einem Bildschirm. Dieselbe Begruendung
 * steht woertlich in `PODetail.jsx` fuer die MOQ-Sammelaussage.
 *
 * ⚠️ **Der Text nennt die ZEILE** (`art0@B: Creating…`). „Creating…" allein
 * waere bei zwei Laeufen zweimal derselbe Satz — genau der Defekt, den Luecke 2
 * fuer den Fehlerslot behebt, nur auf der Hoerflaeche.
 *
 * Die Reihenfolge ist die der Abbildung, also die Reihenfolge des Starts.
 */
export function laufendeAnsage(laufend) {
  const karte = laufend || {};
  return Object.keys(karte)
    .map((schluessel) => {
      const text = fortschrittText(karte[schluessel]);
      return text ? `${schluessel}: ${text}` : null;
    })
    .filter(Boolean)
    .join(' · ');
}



/**
 * Darf diese Zeile einen Rabatt bekommen — und ist der Weg dorthin lebendig?
 *
 * 🔴 **Der Rabatt ist die BEDINGUNG des Anlegens, nicht eine Kuer.**
 * `lege_listing_an` bricht **vor jedem Shopify-Schritt** ab, wenn der Kladde der
 * bestaetigte Rabatt fehlt (`backend/second_life.py:994-995` → 409
 * `kein_rabatt`), und `approve` rechnet den Preis aus demselben Rabatt
 * (`:1776`). Auf prod sind **12 von 13** Kladdenzeilen `discovered` — der Fall
 * ist der **Normalfall**, nicht die Ausnahme. Ohne diese Regel hat der Satz
 * „Without a confirmed discount no draft is created. Please confirm the
 * discount first." auf dieser Seite kein Ziel: das
 * Karussell ist der einzige Ort mit „Create", und der Klick endete in einer
 * Absage (Grill-Blocker B1, 2026-10-09).
 *
 * 🔴 **Drei Bedingungen, und alle drei sind noetig:**
 *   * `anlegbar` — B/C **und** A-Produkt vorhanden. DEF wird nicht verkauft,
 *     und ohne A-Produkt gibt es weder A-Preis noch EK (`pruefe_rabatt`
 *     antwortet `kein_grundprodukt`).
 *   * `kind === 'aggregate'` — `POST /<id>/rabatt` antwortet einer
 *     Einheiten-Zeile **409 `keine_aggregat_zeile`**, und `lege_listing_an`
 *     legt sie nicht an. Fail-closed: ein fehlendes `kind` ist **keine**
 *     Erlaubnis (dieselbe Lesart wie `second_life_bestand._listing_index`).
 *   * Zustand `none` oder `draft` — nur diese zwei kennt der Weg weiter
 *     (`lege_listing_an` verlangt `discovered`, `approve` einen Entwurf). Auf
 *     einer `live`-Zeile schriebe der Rabatt **nur die Datenbank** um, waehrend
 *     im Shop der alte Preis stehen bleibt — die zwei liefen auseinander.
 *
 * 🔴 **NACHTRAG 2026-10-14: eine Zeile OHNE Kladde darf auch** — und der Grund
 * ist nicht Bequemlichkeit, sondern die Reihenfolge des Weges. Owner: *„Wir
 * brauchen keinen Case, damit wir eine B-Ware anlegen duerfen."* Bis hierher
 * hiess `id == null` „kein Rabatt moeglich", und weil der Rabatt die Bedingung
 * des Anlegens ist, war die Zeile damit doppelt gesperrt — ohne dass einer der
 * beiden Gruende einen Weg hatte.
 *
 * ⚠️ **Warum das kein Aufweichen der Fail-closed-Regel ist.** Die beiden
 * Lagen sind NICHT dieselbe:
 *   * `id != null && kind !== 'aggregate'` — es GIBT eine Zeile, und sie ist
 *     eine Einheiten-Zeile. `POST /<id>/rabatt` lehnt sie ab. Bleibt gesperrt.
 *   * `id == null` — es gibt **gar keine** Zeile. `POST /api/second-life/kladde`
 *     legt fuer dieses (Artikel, Stufe) eine **Aggregat**-Zeile an
 *     (`second_life.lege_kladde_an`), und die ist genau die Zeilenform, die der
 *     Rabattweg verlangt. Der Weg existiert also — er beginnt nur einen Schritt
 *     frueher.
 *
 * 🔴 **Der Zustand einer kladdenfreien Zeile ist immer `none`.** Er kommt aus
 * der Kladde (`_anzeige_zustand`), und ohne Kladde gibt es ihn nicht. Die
 * Pruefung steht trotzdem als eigene Zeile: sie ist die Zulassung, und eine
 * spaetere Aenderung an `_anzeige_zustand` soll sie nicht still mitziehen.
 *
 * 🔴 **Und die Zulassung gilt NUR, wenn keine Zeilenart dran steht.**
 * `zeile.kind == null` ist die Bedingung, nicht „`id == null` allein": eine
 * Zeile, die sich als `unit` ausweist, bleibt gesperrt, auch wenn sie keine
 * Kennung traegt. In der Route haengen `id` und `kind` an derselben Kladdenzeile
 * (`_bau` liest beide aus `l`) — die zwei koennen also nur zusammen fehlen. Die
 * Wache steht trotzdem: sie ist die Fail-closed-Haelfte derselben Regel, und
 * eine Probe (`SecondLifeBestand.test.jsx` L.4b) haelt sie fest.
 */
export function rabattMoeglich(zeile) {
  if (!zeile || zeile.anlegbar !== true) return false;
  if (zeile.id == null) return zeile.kind == null && zeile.state === 'none';
  if (zeile.kind !== 'aggregate') return false;
  return zeile.state === 'none' || zeile.state === 'draft';
}

// Die Shopify-Admin-Basis. Lokale Konstante statt Import der ganzen
// Produktseite.
const SHOPIFY_ADMIN = (process.env.REACT_APP_SHOPIFY_ADMIN_URL
  || 'https://admin.shopify.com/store/kaffeemanuale').replace(/\/+$/, '');

/**
 * Traegt die Zeile ein Shopify-Produkt?
 *
 * 🔴 **Die EINE Bedingung, an der zwei Dinge haengen** (Regel-15-Befund der
 * Nachbesserung 3): das **Ziel** des Absprungs (`shopifyProduktUrl` unten) und
 * sein **Etikett** (`OpenCases.jsx` — „Open in Shopify" gegen „Find in
 * Shopify", Hausform `ProductDetail.jsx:564/957`). Zwei Abschriften derselben
 * Bedingung hiessen: ein Etikett, das ein anderes Ziel verspricht, als der
 * Klick oeffnet. Deshalb steht sie hier und wird von beiden gelesen.
 *
 * Eine **leere** Kennung zaehlt wie keine — der Server kann `""` liefern, und
 * ein Etikett „Open in Shopify" ueber einer Suche waere die falsche Auskunft.
 */
export function hatShopifyProdukt(zeile) {
  const pid = zeile?.shopify_product_id;
  return pid != null && String(pid).trim() !== '';
}

/**
 * Der Shopify-Absprung je Zeile — das PRODUKT, wenn die Zeile seine Kennung
 * traegt, sonst die Suche nach der Artikelnummer.
 *
 * 🔴 **Spec §5.2 verlangt das Produkt, nicht die Suche** (Grill W4 der Runde 4).
 * `shopify_product_id` stand bis dahin nicht im Zeilenvertrag; die Backend-Bahn
 * traegt es nach, und die Seite benutzt es, **sobald es da ist** — ohne darauf
 * zu warten. Fehlt es (oder ist es leer), bleibt die Suche der Rueckfall: sie
 * findet das A-Produkt ueber seine Nummer, und eine Zeile ohne Produkt hat
 * ohnehin keins zum Anspringen.
 *
 * Eine Shopify-GID (`gid://shopify/Product/123`) und eine blosse Zahl fuehren
 * auf dieselbe Adresse — genommen wird das letzte Segment.
 */
export function shopifyProduktUrl(zeile) {
  if (hatShopifyProdukt(zeile)) {
    const nackt = String(zeile.shopify_product_id).split('/').pop();
    return `${SHOPIFY_ADMIN}/products/${encodeURIComponent(nackt)}`;
  }
  const nummer = zeile?.basis || zeile?.sku;
  return `${SHOPIFY_ADMIN}/products?query=${encodeURIComponent(nummer || '')}`;
}
